import { useMemo } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "@/components/phone-field.css";
import { AlertCircle } from "lucide-react";

// Pays privilégiés en tête de liste (Afrique de l'Ouest & centrale + FR)
const PREFERRED = ["CI", "ML", "SN", "BF", "TG", "BJ", "NE", "GN", "CM", "GA", "CD", "CG", "FR"];

/**
 * Champ de saisie de numéro international avec drapeau + indicatif pays (+225, +223…)
 * et validation par pays. Stocke la valeur au format E.164 ("+2250140456948").
 *
 * Props:
 *  - value / onChange(value)   : valeur E.164
 *  - label, hint, testId, required, defaultCountry (def. "CI")
 *  - showError (def. true)     : affiche le message d'erreur si numéro invalide
 */
export default function PhoneField({
  value,
  onChange,
  label = "Téléphone",
  hint,
  testId = "phone",
  required = false,
  defaultCountry = "CI",
  showError = true,
  className = "",
}) {
  const invalid = useMemo(() => {
    if (!value) return false;
    try {
      return !isValidPhoneNumber(value);
    } catch {
      return true;
    }
  }, [value]);

  return (
    <div className={`block ${className}`} data-testid={`phone-field-${testId}`}>
      {label && (
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
          {label}
          {required && <span className="ml-0.5 text-rose-500">*</span>}
        </span>
      )}
      <div className={`ismo-phone ${invalid ? "ismo-phone-invalid" : ""}`}>
        <PhoneInput
          international
          countryCallingCodeEditable={false}
          defaultCountry={defaultCountry}
          countryOptionsOrder={[...PREFERRED, "|", "..."]}
          value={value || undefined}
          onChange={(v) => onChange(v || "")}
          numberInputProps={{ "data-testid": `phone-input-${testId}` }}
          placeholder="Numéro de téléphone"
        />
      </div>
      {hint && !invalid && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
      {showError && invalid && (
        <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-rose-600" data-testid={`phone-error-${testId}`}>
          <AlertCircle className="h-3.5 w-3.5" />
          Numéro invalide pour ce pays — vérifiez l'indicatif et le nombre de chiffres.
        </span>
      )}
    </div>
  );
}

export { isValidPhoneNumber };
